import React, { useState } from "react";
import { BtmReturnRequest } from "../model/btm-return-request";
import { VPUITheme } from "../../../../common/theme/vpui-theme";
import { Button, Paper, Typography } from "@mui/material";
import { AddTask, Block, Task, TaskAlt, Troubleshoot } from "@mui/icons-material";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { BtmReturnInspectionProduct, BtmReturnInspectionStatus } from "../model/btm-return-inspection-product";
import { BtmRequestCustomInspectionDialog } from "../btm-request-custom-inspection-dialog/btm-request-custom-inspection-dialog";
import { SubmitReturnRequestInspectionHandler } from "../cqrs/submit-return-request-inspection/submit-return-request-inspection-handler";
import { BtmBulkyFailedInspectionDialog } from "../btm-failed-inspection-dialog/btm-bulky-failed-inspection-dialog";
import { t } from "i18next";

interface IBtmRequestInspectionProps {
    request: BtmReturnRequest;
    onCompleted: (request: BtmReturnRequest) => void;
}

export const BtmRequestInspection = (props: IBtmRequestInspectionProps) => {
    const [customInspectionDialogOpen, setCustomInspectionDialogOpen] = useState(false);
    const [bulkyFailedInspectionDialogOpenFor, setBulkyFailedInspectionDialogOpenFor] =
        useState<BtmReturnInspectionStatus | undefined>(undefined);

    const handleBulkAccept = async () => {
        await handleBulkInspectionSave(props.request.declaredProducts.flatMap(BtmReturnInspectionProduct.fromReportedProduct)
            .map(inspection => inspection.inspect(BtmReturnInspectionStatus.Accepted)));
    };

    const handleBulkInspectionSave = async (data: BtmReturnInspectionProduct[]) => {
        try {
            await handleFinishInspection(data);

            if (data.find(e => e.status !== data[0].status) === undefined) {
                switch (data[0].status) {
                    case BtmReturnInspectionStatus.Accepted:
                        notificationModel.addSuccessMessage(t("components.blueBrandReturns.page.details.inspection.form.acceptedSuccess"));
                        return;
                    case BtmReturnInspectionStatus.ConditionallyAccepted:
                        notificationModel.addSuccessMessage(t("components.blueBrandReturns.page.details.inspection.form.conditionallyAcceptedSuccess"));
                        return;
                    case BtmReturnInspectionStatus.Refused:
                        notificationModel.addSuccessMessage(t("components.blueBrandReturns.page.details.inspection.form.refusedSuccess"));
                        return;
                }
            } else {
                notificationModel.addSuccessMessage(t("components.blueBrandReturns.page.details.inspection.form.saveSuccess"));
            }
        } catch (error) {
            notificationModel.addErrorMessage(t("components.blueBrandReturns.page.details.inspection.form.saveError"));
        }
    };

    const handleFinishInspection = async (inspectionDetails: BtmReturnInspectionProduct[]) => {
        props.onCompleted(await SubmitReturnRequestInspectionHandler(props.request,
            inspectionDetails.filter(item => item.status !== undefined)));
    };

    return <Paper elevation={0} sx={{ borderColor: VPUITheme.palette.primary.main }}
                  className={"btm-request-details__section btm-request-details__section--main"}>
        {customInspectionDialogOpen &&
            <BtmRequestCustomInspectionDialog request={props.request}
                                              onCompleted={items => handleFinishInspection(items)}
                                              onClose={() => setCustomInspectionDialogOpen(false)} />}
        {bulkyFailedInspectionDialogOpenFor &&
            <BtmBulkyFailedInspectionDialog data={props.request.inspectionResult.length > 0 ?
                props.request.inspectionResult :
                props.request.declaredProducts.flatMap(BtmReturnInspectionProduct.fromReportedProduct)}
                                            status={bulkyFailedInspectionDialogOpenFor}
                                            onCompleted={items => handleBulkInspectionSave(items)}
                                            onClose={() => setBulkyFailedInspectionDialogOpenFor(undefined)} />}
        <Typography variant={"h4"} color={"textPrimary"} className={"btm-request-details__title"}>
            <Task color={"primary"} fontSize={"large"} />
            <span className={"btm-request-details__title-group"}>
                <span>{t("components.blueBrandReturns.page.details.inspection.title")}</span>
                <Typography component={"span"} className={"btm-request-details__title-small"}
                            fontSize={"x-small"} color={"textSecondary"}>
                    {t("components.blueBrandReturns.page.details.inspection.description")}
                </Typography>
            </span>
        </Typography>
        <div className={"btm-request-details__table-buttons"}>
            <Button onClick={handleBulkAccept}
                    startIcon={<TaskAlt />} variant={"outlined"} size={"large"} color={"success"}>
                {t("components.blueBrandReturns.page.details.inspection.action.accept")}
            </Button>
            <Button onClick={() => setBulkyFailedInspectionDialogOpenFor(BtmReturnInspectionStatus.ConditionallyAccepted)}
                    startIcon={<AddTask />} variant={"outlined"} size={"large"} color={"warning"}>
                {t("components.blueBrandReturns.page.details.inspection.action.conditionalAccept")}
            </Button>
            <Button onClick={() => setBulkyFailedInspectionDialogOpenFor(BtmReturnInspectionStatus.Refused)}
                    startIcon={<Block />} variant={"outlined"} size={"large"} color={"error"}>
                {t("components.blueBrandReturns.page.details.inspection.action.reject")}
            </Button>
            <Button onClick={() => setCustomInspectionDialogOpen(true)}
                    startIcon={<Troubleshoot />} variant={"outlined"} size={"large"} color={"secondary"}>
                {t("components.blueBrandReturns.page.details.inspection.action.detailedInspection")}
            </Button>
        </div>
    </Paper>;
};
